/**
 * Sprint 23.5 — Hire wizard shared types.
 *
 * The wizard draft mirrors the persona fields on the worker `AgentConfig`
 * (src/engine/library.ts) plus the basics needed to create the agent
 * (name, role, parent_id). All optional except name + role, which the Hire
 * button gates on. The composition AI suggests values via the
 * `update_composition_draft` tool; the chrome intercepts each call and
 * patches this shape locally.
 */

export type HirePersonaStyle = "matter-of-fact" | "conversational" | "playful" | "formal";

export interface HireDraft {
  /** Agent name (required for Hire to enable). */
  name: string;
  /** Role / one-line job title (required for Hire to enable). */
  role: string;
  /** Long-form bio. */
  bio: string;
  /** Curated style enum, or null for "no opinion". */
  style: HirePersonaStyle | null;
  /** Free-text voice / tone description. */
  voice_tone: string;
  /** What this agent cares about / optimises for. */
  motivations: string;
  /** Free-text specialty tags. */
  specialties: string[];
  /** Parent agent id; null = top-level under the workspace root. */
  reports_to: string | null;
  /**
   * Which workspace MCP connections this agent can use. "all" → every
   * connection; string[] → specific connection ids; [] → none.
   */
  equipped_with: "all" | string[];
}

export function emptyHireDraft(): HireDraft {
  return {
    name: "",
    role: "",
    bio: "",
    style: null,
    voice_tone: "",
    motivations: "",
    specialties: [],
    reports_to: null,
    equipped_with: [],
  };
}

/** Field-name → value patch the composition AI emits via tool calls. */
export type HireDraftPatch = Partial<HireDraft>;

/** Apply a single tool-call patch to the local draft state. */
export function applyDraftPatch(prev: HireDraft, field: string, rawValue: unknown): HireDraft {
  switch (field) {
    case "name":
    case "role":
    case "bio":
    case "voice_tone":
    case "motivations":
      return { ...prev, [field]: typeof rawValue === "string" ? rawValue : "" };
    case "style": {
      const allowed: HirePersonaStyle[] = ["matter-of-fact", "conversational", "playful", "formal"];
      const v = typeof rawValue === "string" && (allowed as string[]).includes(rawValue)
        ? (rawValue as HirePersonaStyle)
        : null;
      return { ...prev, style: v };
    }
    case "specialties":
      return {
        ...prev,
        specialties: Array.isArray(rawValue) ? rawValue.filter((s): s is string => typeof s === "string") : [],
      };
    case "reports_to":
      return { ...prev, reports_to: typeof rawValue === "string" ? rawValue : null };
    case "equipped_with":
      if (rawValue === "all") return { ...prev, equipped_with: "all" };
      if (Array.isArray(rawValue)) {
        return {
          ...prev,
          equipped_with: rawValue.filter((s): s is string => typeof s === "string"),
        };
      }
      return prev;
    default:
      return prev;
  }
}

/** Hire is enabled the moment the basics are present. */
export function canHire(draft: HireDraft): boolean {
  return draft.name.trim().length > 0 && draft.role.trim().length > 0;
}
